import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import DifficultySelector, { GradeRange, getAvailableGrades, getDifficultyNumbers } from "@/components/DifficultySelector";
import { ArrowLeft } from "lucide-react";
import type { Topic } from "@shared/schema";
import { useSubject } from "@/contexts/SubjectContext";
import { getYearCategory, CATEGORY_ORDER, isSubjectMatch, formatYearGroup } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
export default function CustomQuiz() {
  const [, setLocation] = useLocation();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [questionCount, setQuestionCount] = useState<number>(10);
  const [difficulty, setDifficulty] = useState<GradeRange[]>(['1-4', '5-6', '7-8', '9']);

  const { currentSubject } = useSubject();

  useEffect(() => {
    if (!currentSubject) return;

    const loadTopics = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/topics?mode=quiz&subject=${currentSubject.id}`);
        if (response.ok) {
          const data = await response.json();
          setTopics(data.filter((t: Topic) => isSubjectMatch(t, currentSubject.id)));
        }
      } catch (error) {
        console.error('Failed to load topics:', error);
      } finally {
        setLoading(false);
      }
    };
    loadTopics();
  }, [currentSubject]);

  const toggleTopic = (topicId: string) => {
    setSelectedTopics(prev =>
      prev.includes(topicId)
        ? prev.filter(id => id !== topicId)
        : [...prev, topicId]
    );
  };

  const getTopicQuestionCount = (topic: Topic) => {
    if (!topic.questions) return 0;
    const difficultyRange = getDifficultyNumbers(difficulty);
    return topic.questions.filter(q => difficultyRange.includes(q.difficulty)).length;
  };

    const maxAvailableQuestions = Math.max(1, selectedTopics.length > 0 ? topics.filter(t => selectedTopics.includes(t.id)).reduce((sum, t) => sum + getTopicQuestionCount(t), 0) : 30);

  const handleStartCustomQuiz = () => {
    if (selectedTopics.length > 0) {
      const topicIds = selectedTopics.join(',');
      setLocation(`/custom-quiz/start?topics=${topicIds}&count=${questionCount}&difficulty=${difficulty.join(",")}`);
    }
  };

  const groupedTopics = Array.from(new Map(topics.map(t => [t.id, t])).values()).reduce((acc: Record<string, Record<string, Record<string, Topic[]>>>, topic: Topic) => {
    const cat = getYearCategory(topic.yearLevel);
    if (!acc[cat]) acc[cat] = {};
    const yearStr = formatYearGroup(topic.yearLevel);
    if (!acc[cat][yearStr]) acc[cat][yearStr] = {};
    const subCat = topic.category || "Uncategorized";
    if (!acc[cat][yearStr][subCat]) acc[cat][yearStr][subCat] = [];
    acc[cat][yearStr][subCat].push(topic);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setLocation("/")}
              data-testid="button-back"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold font-serif">Custom Quiz Builder</h1>
              <p className="text-muted-foreground mt-1">
                Mix topics and set your own question count
              </p>
            </div>
          </div>

          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Question Count: {questionCount}</h3>
            <Slider
              value={[questionCount]}
              onValueChange={(val) => setQuestionCount(val[0])}
              min={5}
              max={30}
              step={5}
              className="w-full"
            />
            <p className="text-sm text-muted-foreground mt-2">
              Select between 5 and 30 questions
            </p>
          </Card>

          <DifficultySelector
                  selected={difficulty}
                  availableGrades={getAvailableGrades(topics.filter(t => selectedTopics.includes(t.id)).flatMap(t => t.questions))}
                  onChange={setDifficulty}
                />

          <div>
            <h3 className="text-lg font-semibold mb-4">Select Topics ({selectedTopics.length} selected)</h3>
            <div className="space-y-6">
              {loading ? (
                  <div className="flex justify-center p-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
              ) : (
                <Accordion type="multiple" className="w-full space-y-4">
                  {CATEGORY_ORDER.filter(cat => groupedTopics[cat]).map(category => (
                    <AccordionItem key={category} value={category} className="border bg-card rounded-lg px-4 shadow-sm">
                      <AccordionTrigger className="hover:no-underline font-serif text-xl py-4">
                        {category}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-8 pt-2 pb-4">
                          {Object.entries(groupedTopics[category])
                            .sort(([yearA], [yearB]) => yearA.localeCompare(yearB, undefined, {numeric: true}))
                            .map(([yearStr, subGroups]) => (
                            <div key={yearStr} className="space-y-4">
                              <h3 className="text-lg font-bold border-b pb-2">{yearStr}</h3>
                              <div className="space-y-6">
                                {Object.entries(subGroups)
                                  .sort(([catA], [catB]) => catA.localeCompare(catB))
                                  .map(([catName, topicsList]) => (
                                  <div key={catName}>
                                    <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">{catName}</h4>
                                    <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                                      {topicsList.map((topic) => {
                                        const count = getTopicQuestionCount(topic);
                                        const disabled = count === 0;
                                        return (
                                          <Card
                                            key={topic.id}
                                            className={`p-4 transition-all ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover-elevate active-elevate-2'}`}
                                            onClick={() => !disabled && toggleTopic(topic.id)}
                                          >
                                            <div className="flex items-center gap-3">
                                              <Checkbox
                                                className="pointer-events-none"
                                                checked={selectedTopics.includes(topic.id)}
                                                disabled={disabled}
                                                data-testid={`checkbox-topic-${topic.id}`}
                                              />
                                              <Label className={`flex-1 font-medium ${disabled ? '' : 'cursor-pointer pointer-events-none'}`}> 
                                                {topic.name}
                                              </Label>
                                              <span className="text-sm text-muted-foreground">      
                                                {count} matching questions
                                              </span>
                                            </div>
                                          </Card>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>
          </div>

          <div className="flex justify-center pt-4">
            <Button
              size="lg"
              onClick={handleStartCustomQuiz}
              disabled={selectedTopics.length === 0}
              data-testid="button-start-custom-quiz"
              className="w-full sm:w-auto"
            >
              Start Custom Quiz ({selectedTopics.length} {selectedTopics.length === 1 ? 'topic' : 'topics'})
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}






